import { memo, useMemo } from 'react';
import moment from 'moment';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { CMSBanner } from 'types/template';
// Import Constants
import { l, DateTimeFormat } from 'constants/common';

interface CMSBannerInfoProps {
    body: string;
}

const CMSBannerInfo = ({ body }: CMSBannerInfoProps) => {
    const { t } = useTranslation();

    const bannerData = useMemo<Nullable<CMSBanner>>(() => {
        try {
            return JSON.parse(body);
        } catch {
            return null;
        }
    }, [body]);

    if (!bannerData) return null;

    const getPlatformLabel = (platforms: CMSBanner['platforms']) => {
        if (isNil(platforms)) return t(l.NA);
        if (platforms.web && platforms.mobile) return t(l.Both);
        if (platforms.web) return t(l.Web);
        if (platforms.mobile) return t(l.Mobile);
        return t(l.NA);
    };

    const formatDate = (date: Nullable<string> | undefined) => {
        if (!date) return t(l.NA);
        const parsed = moment(date);
        return parsed.isValid() ? parsed.format(DateTimeFormat) : t(l.NA);
    };

    return (
        <div className="crm-object-modal-head">
            <div className="title-wrapper">
                <p className="title-wrapper-text">{t(l.BannerInfo)}</p>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.BannerId)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{bannerData.id}</span>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.BannerTitle)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{bannerData.title}</span>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Platform)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{getPlatformLabel(bannerData.platforms)}</span>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.Component)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{bannerData.components?.title ?? t(l.NA)}</span>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.BannerStartDate)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{formatDate(bannerData.start_date)}</span>
            </div>

            <div className="key-value-h">
                <span className="ellipsis-text kv-label">{t(l.BannerEndDate)}</span>
                <span className="ellipsis-text kv-value kv-value-css">{formatDate(bannerData.end_date)}</span>
            </div>
        </div>
    );
};

export default memo(CMSBannerInfo);
